import React, { useEffect, useState, useMemo, useCallback } from "react";
import ButtonSecondary from "../buttons/ButtonSecondary";
import NavigationMyFiles from "../dashboard/utiles/NavigationMyFiles";
import ContentFiles from "../dashboard/utiles/ContentFiles";
import ModalCreateFolder from "./modalCreateFolder";
import ModalUploadFile from "./modalUploadFile";
import { toast } from "react-toastify";
import useMessagesApi from "../../hooks/api/useMessagesApi";

const ModalAttachFiles = ({ closeModal, titleModal, onSelectFolders, initialSelection = [] }) => {
  const [sortBy, setSortBy] = useState("Date (newest)");
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [folderStructure, setFolderStructure] = useState({});
  const [selectedFolders, setSelectedFolders] = useState([]);
  const { fetchOutboxFolderStructure } = useMessagesApi();

  // Fetch folder structure from outbox/get-folder-structure for messages
  useEffect(() => {
    const fetchFolderStructure = async () => {
      try {
        const sanitizedStructure = await fetchOutboxFolderStructure();
        setFolderStructure(sanitizedStructure);
      } catch (error) {
        console.error("Error fetching folder structure:", error);
        const errorMessage = error?.response?.data?.error || error?.message || "Error fetching folder structure";
        toast.error(errorMessage);
      }
    };

    fetchFolderStructure();
  }, [fetchOutboxFolderStructure]);

  const handleCheckboxChange = useCallback((item, isChecked) => {
    const folderName =
      typeof item === "string"
        ? item
        : item?.path || item?.fullPath || item?.id || item?.name || "";

    const normalizedName = folderName.replace(/^\/+/, "").replace(/\/+$/, "");

    setSelectedFolders((prev) => {
      if (isChecked) {
        if (prev.includes(normalizedName)) {
          return prev;
        }
        return [...prev, normalizedName];
      }
      return prev.filter((name) => name !== normalizedName);
    });
  }, []);

  const refreshFolderStructure = async () => {
    try {
      const sanitizedStructure = await fetchOutboxFolderStructure();
      setFolderStructure(sanitizedStructure);
      return sanitizedStructure;
    } catch (error) {
      console.error("Error refreshing folder structure:", error);
      const errorMessage = error?.response?.data?.error || error?.message || "Error refreshing folder structure";
      toast.error(errorMessage);
      throw error;
    }
  };

  useEffect(() => {
    if (initialSelection && initialSelection.length > 0) {
      const normalizedPaths = initialSelection.map(path => {
        if (!path || path.trim() === '') return null;
        let normalized = path.trim().replace(/^\/+/, '').replace(/\/+$/, '');
        return normalized;
      }).filter(Boolean);
      
      normalizedPaths.forEach(path => {
        if (!selectedFolders.includes(path)) {
          handleCheckboxChange(path, true);
        }
      });
    }
  }, [initialSelection, selectedFolders, handleCheckboxChange]);


  const currentStructure = useMemo(() => {
    if (!currentFolderPath) {
      return folderStructure;
    }

    let currentStructure = folderStructure;
    const pathParts = currentFolderPath.split('/').filter(Boolean);
    
    for (const part of pathParts) {
      const folderKey = Object.keys(currentStructure).find(
        key => key.replace(/\/+$/, '') === part || key === part
      );
      
      if (currentStructure && folderKey && currentStructure[folderKey]) {
        currentStructure = currentStructure[folderKey];
        if (typeof currentStructure === 'object' && currentStructure !== null && 'isFolder' in currentStructure) {
          const { isFolder, ...rest } = currentStructure;
          currentStructure = rest;
        }
      } else {
        return {};
      }
    }

    return currentStructure || {};
  }, [currentFolderPath, folderStructure]);

  const folders = Object.entries(currentStructure)
    .filter(([key, value]) => {
      if (key === 'isFolder' || key === 'hasFiles' || key === 'path' || key === 'lastModified' || key === 'size' || key === 'etag') {
        return false;
      }
      return value && typeof value === 'object' && value.isFolder === true;
    })
    .map(([name, value]) => {
      const hasFiles = Object.keys(value).some(
        key => key !== 'isFolder' && key !== 'hasFiles' && key !== 'path' && key !== 'lastModified' && key !== 'size' && key !== 'etag' && 
        value[key] && typeof value[key] === 'object' && value[key].isFolder !== true
      );

      const sanitizedName = name.replace(/\/+$/, '');
      const fullPath = value?.path || `/${sanitizedName}/`;
      
      return {
        id: sanitizedName,
        name: sanitizedName, 
        type: "folder",
        date: value?.lastModified || "",
        size: value?.size ? parseInt(value.size, 10) : 0,
        structure: value,
        hasFiles: hasFiles,
        path: fullPath,
        fullPath,
      };
    });

  const files = Object.entries(currentStructure)
    .filter(([key, value]) => {
      if (key === 'isFolder' || key === 'hasFiles' || key === 'path' || key === 'lastModified' || key === 'size' || key === 'etag') {
        return false;
      }
      return value && typeof value === 'object' && value.isFolder === false;
    })
    .map(([name, value]) => {
      const extension = name.split('.').pop()?.toLowerCase() || '';
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
      const isImage = imageExtensions.includes(extension);
      const fullPath = value?.path || `/${name}`;
      
      return {
        id: name,
        name,
        type: isImage ? "image" : "file",
        size: value?.size ? parseInt(value.size, 10) : 0,
        date: value?.lastModified || "",
        previewUrl: `${name}`,
        folderPath: null,
        path: fullPath,
        fullPath,
      };
    });

  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
  };

  const handleCancel = () => {
    closeModal();
  };

  const handleUpdate = () => {
    onSelectFolders(selectedFolders);
    closeModal();
  };

  const handleCloseCreateFolderModal = () => {
    setIsCreateFolderModalOpen(false);
    refreshFolderStructure();
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
    refreshFolderStructure();
  };

  const getCurrentPath = () => {
    return currentFolderPath || "";
  };

  const handleFolderNavigation = (folder) => {
    if (!folder || folder.id === "" || folder.id === undefined) {
      setCurrentFolderPath("");
      return;
    }
    const newPath = currentFolderPath 
      ? `${currentFolderPath}/${folder.id}` 
      : folder.id;
    setCurrentFolderPath(newPath);
  };
  
  const handleBreadcrumbNavigation = (breadcrumbPath) => {
    setCurrentFolderPath(breadcrumbPath || "");
  };

  const handleModalCheckboxChange = (itemId, isChecked) => {
    handleCheckboxChange(itemId, isChecked);
  };

  return (
    <div className="fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-75 flex justify-center items-center z-50 p-4">
      <div className="bg-white mobile:w-full w-full max-w-6xl h-full max-h-[90vh] rounded-lg flex flex-col">
        <div className="flex items-center justify-between p-6 border-b-2 border-b-stroke">
          <h2 className="text-h6 text-dark4 text-start">
            {titleModal}
          </h2>
          <div className="flex items-center gap-4">
            <button
              onClick={handleCancel}
              className="text-dark4 hover:text-red-600 transition-colors p-1"
              aria-label="Close modal"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          <NavigationMyFiles
            selectedFolders={selectedFolders}
            onSortChange={handleSortChange}
            folderStructure={folderStructure}
            onFileClick={(item) => {
              // Navigate to the folder/file in the modal
              if (item.isFolder) {
                // Extract folder path from item.path
                const folderPath = item.path.replace(/^\//, "").replace(/\/+$/, "");
                setCurrentFolderPath(folderPath);
              } else {
                // For files, navigate to the parent folder
                const filePath = item.path.replace(/^\//, "");
                const pathParts = filePath.split("/");
                if (pathParts.length > 1) {
                  pathParts.pop(); // Remove filename
                  const folderPath = pathParts.join("/");
                  setCurrentFolderPath(folderPath);
                } else {
                  setCurrentFolderPath(""); // Root folder
                }
              }
            }}
          />
          <div className="overflow-y-auto flex-1">
            <ContentFiles
              selectedFolders={selectedFolders}
              handleCheckboxChange={handleModalCheckboxChange}
              sortBy={sortBy}
              folders={folders}
              files={files}
              refreshFolderStructure={refreshFolderStructure}
              rootStructure={folderStructure}
              onFolderClick={(folder) => {
                if (folder.isBreadcrumb) {
                  handleBreadcrumbNavigation(folder.id);
                } else {
                  handleFolderNavigation(folder);
                }
              }}
              currentPath={currentFolderPath}
            />
          </div>
        </div>

        <div className="flex-row flex gap-4 mobile:pt-8 desktop:pt-6 cursor-pointer border-t-2 border-t-stroke p-6 justify-end">
          <ButtonSecondary
            text="Cancel"
            customClass="bg-white border text-dark4 text-center justify-center"
            width="mobile:w-full desktop:w-[125px]"
            padding="px-[5px] py-[10px]"
            onClick={handleCancel}
          />
          <ButtonSecondary
            text="Attach"
            customClass="bg-primary text-white text-center justify-center"
            width="mobile:w-full desktop:w-[125px]"
            padding="px-[5px] py-[10px]"
            onClick={handleUpdate}
          />
        </div>
      </div>

      <ModalCreateFolder
        isOpen={isCreateFolderModalOpen}
        onClose={handleCloseCreateFolderModal}
        selectedFolder={getCurrentPath()}
        currentPath={getCurrentPath()}
        onFolderCreated={refreshFolderStructure}
      />

      <ModalUploadFile
        selectedFolder={getCurrentPath()}
        isOpen={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        onUploadSuccess={refreshFolderStructure}
      />
    </div>
  );
};

export default ModalAttachFiles;
